/**
 * UploadModal — handles file upload with drag-drop zone, multi-file metadata,
 * upload progress, and partial failure (207) handling.
 */

import React, { useCallback, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { toast } from "react-toastify";

import { UploadFileItem, UploadResultItem } from "./types";

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (formData: FormData) => Promise<void>;
  folderId?: number | null;
}

export const UploadModal: React.FC<UploadModalProps> = ({
  open,
  onClose,
  onUpload,
  folderId,
}) => {
  const [files, setFiles] = useState<UploadFileItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<UploadResultItem[]>([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleAddFiles = useCallback((fileList: FileList | File[]) => {
    const newFiles: UploadFileItem[] = Array.from(fileList).map((file) => ({
      file,
      metadata: {
        name: file.name,
        classification: "other",
        status: "",
        tags: [],
      },
    }));
    setFiles((prev) => [...prev, ...newFiles]);
    setUploadResults([]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.files.length > 0) {
        handleAddFiles(e.dataTransfer.files);
      }
    },
    [handleAddFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleUpdateMetadata = useCallback(
    (index: number, field: string, value: string | string[]) => {
      setFiles((prev) =>
        prev.map((item, i) =>
          i === index
            ? { ...item, metadata: { ...item.metadata, [field]: value } }
            : item,
        ),
      );
    },
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (files.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);
    setUploadResults([]);

    const formData = new FormData();

    files.forEach((item, index) => {
      // Nest all fields under files[index][*] so Laravel's $request->input('files')
      // returns a structured array the controller can iterate over.
      formData.append(`files[${index}][file]`, item.file);
      formData.append(`files[${index}][name]`, item.metadata.name);
      formData.append(`files[${index}][classification]`, item.metadata.classification);
      if (folderId != null) {
        formData.append(`files[${index}][folder_id]`, String(folderId));
      }
      if (item.metadata.status) {
        formData.append(`files[${index}][status]`, item.metadata.status);
      }
      if (item.metadata.tags && item.metadata.tags.length > 0) {
        formData.append(`files[${index}][tags]`, JSON.stringify(item.metadata.tags));
      }
    });

    try {
      await onUpload(formData);
      // Success — modal will be closed by parent via onClose after success
      setFiles([]);
      setUploadProgress(100);
    } catch (err: any) {
      // Partial failure or error
      if (err?.response?.status === 207) {
        const results = err.response?.data?.data as UploadResultItem[];
        if (results) {
          setUploadResults(results);
          const failed = results.filter((r) => r.status === "error");
          if (failed.length > 0) {
            toast.error(`${failed.length} file(s) failed`);
          }
        }
      } else if (err?.code === "ECONNABORTED" || err?.message?.includes("timeout")) {
        toast.error("Upload timed out. Please try again.");
      } else {
        toast.error("Upload failed. Please try again.");
      }
    } finally {
      setIsUploading(false);
    }
  }, [files, folderId, onUpload]);

  const handleClose = useCallback(() => {
    if (isUploading) return;
    setFiles([]);
    setUploadResults([]);
    setUploadProgress(0);
    onClose();
  }, [isUploading, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="upload-modal"
      transitionDuration={0}
    >
      <DialogTitle>Upload Files</DialogTitle>
      <DialogContent>
        {/* Drop zone */}
        <Box
          data-testid="upload-modal-content"
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          sx={{
            border: "2px dashed #dae0e2",
            borderRadius: 2,
            p: 4,
            textAlign: "center",
            bgcolor: "#fafbfb",
            cursor: "pointer",
            mb: 2,
          }}
          onClick={() => {
            const input = document.createElement("input");
            input.type = "file";
            input.multiple = true;
            input.onchange = (e) => {
              if (input.files) handleAddFiles(input.files);
            };
            input.click();
          }}
        >
          <Typography color="textSecondary">
            Drop files here or click to browse
          </Typography>
        </Box>

        {/* Upload progress */}
        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              variant={uploadProgress > 0 ? "determinate" : "indeterminate"}
              value={uploadProgress}
            />
            <Typography variant="caption" sx={{ mt: 0.5, display: "block" }}>
              Uploading... {uploadProgress > 0 ? `${uploadProgress}%` : ""}
            </Typography>
          </Box>
        )}

        {/* File list */}
        {files.length > 0 && (
          <List dense>
            {files.map((item, index) => {
              const result = uploadResults.find((r) => r.file_index === index);
              const hasError = result?.status === "error";
              return (
                <ListItem
                  key={index}
                  secondaryAction={
                    !isUploading && (
                      <Button
                        size="small"
                        color="error"
                        onClick={() => handleRemoveFile(index)}
                      >
                        Remove
                      </Button>
                    )
                  }
                  sx={{
                    bgcolor: hasError ? "#fff0f0" : "transparent",
                    borderRadius: 1,
                    mb: 0.5,
                  }}
                >
                  <ListItemText
                    primary={item.metadata.name}
                    secondary={
                      hasError ? (
                        <Typography variant="caption" color="error">
                          {result?.error || "Upload failed"}
                        </Typography>
                      ) : null
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        {/* Upload results */}
        {uploadResults.length > 0 && (
          <Box data-testid="upload-results" sx={{ mt: 2 }}>
            {uploadResults.map((r, i) => (
              <Typography
                key={i}
                variant="body2"
                data-testid={`upload-result-${i}`}
                sx={{
                  color: r.status === "success" ? "success.main" : "error.main",
                }}
              >
                {r.status === "success"
                  ? `Uploaded: ${r.file?.name || `File ${r.file_index + 1}`}`
                  : `Error: ${r.error || "Unknown error"}`}
              </Typography>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="close-modal"
          onClick={handleClose}
          disabled={isUploading}
        >
          Close
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={files.length === 0 || isUploading}
        >
          {isUploading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
